import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "../components/Logo";
import { Download, LogOut, RefreshCcw, Search, Inbox, Users, Home as HomeIcon, Mail } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import AdminSurvey from "./AdminSurvey";
import AdminAnalytics from "./AdminAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pendli-admin-token";

const ROLE_LABELS = {
  pendler: { label: "Pendler", color: "bg-amber text-ink" },
  huseier: { label: "Huseier", color: "bg-orange text-ink" },
  contact: { label: "Kontakt", color: "bg-cream text-ink" },
};

const SOURCE_TYPE_LABELS = {
  direct: "Direkte",
  organic: "Organisk",
  referral: "Henvisning",
  campaign: "Kampanje",
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` };
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-2.5 h-2.5" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => { document.title = "Pendli Admin — Leads"; }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (search.trim()) params.q = search.trim();
      const [statsRes, leadsRes, dailyRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/leads`, { headers: authHeaders(), params }),
        axios.get(`${API}/admin/leads/daily`, { headers: authHeaders(), params: { days: 14 } }),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setDaily(dailyRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      setError(err?.response?.data?.detail || "Klarte ikke laste leads.");
    } finally {
      setLoading(false);
    }
  }, [roleFilter, search, logout]);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter]);

  // Debounced re-search on text change
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const downloadCsv = async () => {
    try {
      const url = new URL(`${API}/admin/leads.csv`);
      if (roleFilter !== "all") url.searchParams.set("role", roleFilter);
      const res = await axios.get(url.toString(), {
        headers: authHeaders(),
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `pendli-leads-${roleFilter}-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      if (err?.response?.status === 401) return logout();
      setError("Klarte ikke laste ned CSV.");
    }
  };

  const fmtDate = (s) => {
    try {
      const d = new Date(s);
      return d.toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" });
    } catch { return s; }
  };

  const statCards = useMemo(() => stats ? [
    { Icon: Inbox, label: "Totalt", value: stats.total, color: "text-amber" },
    { Icon: Users, label: "Pendlere", value: stats.pendler, color: "text-amber" },
    { Icon: HomeIcon, label: "Huseiere", value: stats.huseier, color: "text-orange" },
    { Icon: Mail, label: "Kontakt", value: stats.contact, color: "text-cream" },
  ] : [], [stats]);

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-ink text-cream">
      {/* Header */}
      <header className="border-b border-amber/15 bg-ink/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LogoMark size={24} />
            <span className="font-display text-lg font-extrabold tracking-tight" style={{ letterSpacing: "-0.04em" }}>
              Pendli · Admin
            </span>
            <span className="hidden sm:inline-block ml-3 text-xs font-bold tracking-widest uppercase text-amber/80">Leads</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="h-9 px-3 border border-amber/30 text-cream/80 hover:text-amber hover:border-amber text-xs font-bold tracking-widest uppercase transition-colors inline-flex items-center gap-1.5"
            >
              <RefreshCcw size={13} /> <span className="hidden sm:inline">Oppdater</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="h-9 px-3 border border-amber/30 text-cream/80 hover:text-orange hover:border-orange text-xs font-bold tracking-widest uppercase transition-colors inline-flex items-center gap-1.5"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Logg ut</span>
            </button>
          </div>
        </div>
      </header>

      {/* Section tabs */}
      <div className="border-b border-amber/10 bg-ink/80">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-1">
          {[
            { v: "leads", l: "Leads" },
            { v: "survey", l: "Dør-til-dør" },
            { v: "analytics", l: "Analytics" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`admin-tab-${t.v}`}
              onClick={() => setTab(t.v)}
              className={`h-12 px-5 text-xs font-bold tracking-widest uppercase transition-colors border-b-2 ${
                tab === t.v ? "border-amber text-amber" : "border-transparent text-cream/50 hover:text-cream"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {tab === "survey" ? (
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <AdminSurvey onUnauthorized={logout} />
        </div>
      ) : tab === "analytics" ? (
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <AdminAnalytics onUnauthorized={logout} />
        </div>
      ) : (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter mb-8" style={{ letterSpacing: "-0.04em" }}>
          Leads-oversikt
        </h1>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="relative bg-amber-deep border border-amber/20 p-5 md:p-6 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br">
              <s.Icon size={20} className={`${s.color} mb-3`} strokeWidth={1.6} />
              <div className="text-xs font-bold tracking-widest uppercase text-cream/60 mb-1">{s.label}</div>
              <div className={`font-display text-3xl md:text-4xl font-extrabold ${s.color}`} style={{ letterSpacing: "-0.04em" }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {stats && (
          <div className="mb-8 text-sm text-cream/50">
            <span className="text-amber font-bold">{stats.last_7_days}</span> nye leads siste 7 dager
          </div>
        )}

        {/* Daily chart */}
        <div className="bg-amber-deep/30 border border-amber/15 p-5 md:p-7 mb-10" data-testid="admin-daily-chart">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
                Leads per dag
              </h2>
              <p className="text-xs text-cream/50 mt-1">Siste 14 dager · stacked etter type</p>
            </div>
            <div className="hidden sm:flex items-center gap-4 text-xs text-cream/70">
              <LegendDot color="#E8A800" label="Pendler" />
              <LegendDot color="#FF6B00" label="Huseier" />
              <LegendDot color="#F5F0E8" label="Kontakt" />
            </div>
          </div>
          <div className="w-full h-56 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="rgba(232,168,0,0.10)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "rgba(245,240,232,0.55)", fontSize: 11, fontFamily: "DM Sans" }}
                  axisLine={{ stroke: "rgba(232,168,0,0.15)" }}
                  tickLine={false}
                  tickFormatter={(d) => {
                    const dt = new Date(d);
                    return dt.toLocaleDateString("nb-NO", { day: "2-digit", month: "short" });
                  }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "rgba(245,240,232,0.45)", fontSize: 11, fontFamily: "DM Sans" }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  cursor={{ fill: "rgba(232,168,0,0.08)" }}
                  contentStyle={{
                    background: "#0D0D0D",
                    border: "1px solid rgba(232,168,0,0.4)",
                    borderRadius: 0,
                    color: "#F5F0E8",
                    fontFamily: "DM Sans",
                    fontSize: 13,
                  }}
                  labelStyle={{ color: "#E8A800", fontWeight: 700, marginBottom: 4 }}
                  labelFormatter={(d) => new Date(d).toLocaleDateString("nb-NO", { weekday: "short", day: "2-digit", month: "short" })}
                />
                <Bar dataKey="pendler" stackId="a" fill="#E8A800" />
                <Bar dataKey="huseier" stackId="a" fill="#FF6B00" />
                <Bar dataKey="contact" stackId="a" fill="#F5F0E8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-amber-deep/40 border border-amber/15 p-4 md:p-5 mb-6 flex flex-col md:flex-row gap-3 md:gap-4 md:items-center" data-testid="admin-filters">
          <div className="flex flex-wrap gap-2">
            {[
              { v: "all", l: "Alle" },
              { v: "pendler", l: "Pendlere" },
              { v: "huseier", l: "Huseiere" },
              { v: "contact", l: "Kontakt" },
            ].map((f) => (
              <button
                key={f.v}
                data-testid={`filter-${f.v}`}
                onClick={() => setRoleFilter(f.v)}
                className={`h-9 px-4 text-xs font-bold tracking-widest uppercase transition-colors border ${
                  roleFilter === f.v
                    ? "bg-amber text-ink border-amber"
                    : "bg-transparent text-cream/70 border-amber/30 hover:border-amber hover:text-amber"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>

          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40 pointer-events-none" />
            <input
              type="text"
              data-testid="admin-search"
              placeholder="Søk på navn, e-post, sted..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-ink border border-amber/20 focus:border-amber h-9 pl-9 pr-3 text-sm text-cream placeholder:text-cream/30 transition-colors"
            />
          </div>

          <button
            onClick={downloadCsv}
            disabled={leads.length === 0}
            data-testid="admin-export-csv"
            className="h-9 px-4 bg-amber text-ink font-bold text-xs tracking-widest uppercase hover:bg-amber-hover transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Download size={14} /> Eksport CSV
          </button>
        </div>

        {/* Table */}
        {error && (
          <div className="bg-orange/10 border border-orange p-4 text-orange mb-6 text-sm" data-testid="admin-error">
            {error}
          </div>
        )}

        <div className="border border-amber/15 overflow-x-auto" data-testid="admin-leads-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-amber-deep/60 text-amber/80 text-xs font-bold tracking-widest uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Dato</th>
                <th className="text-left px-4 py-3 font-bold">Type</th>
                <th className="text-left px-4 py-3 font-bold">Navn</th>
                <th className="text-left px-4 py-3 font-bold">Kontakt</th>
                <th className="text-left px-4 py-3 font-bold">Sted</th>
                <th className="text-left px-4 py-3 font-bold">Melding</th>
                <th className="text-left px-4 py-3 font-bold">Kilde</th>
                <th className="text-left px-4 py-3 font-bold">Kampanje</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-cream/50" data-testid="admin-loading">Laster...</td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-14 text-center text-cream/50" data-testid="admin-empty">
                  Ingen leads ennå{roleFilter !== "all" ? " for dette filteret" : ""}. Snart kommer de første.
                </td></tr>
              )}
              {!loading && leads.map((l) => {
                const r = ROLE_LABELS[l.role] || { label: l.role, color: "bg-cream text-ink" };
                return (
                  <tr key={l.id} className="border-t border-amber/10 hover:bg-amber/5 transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-cream/70 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 text-[10px] font-bold tracking-widest uppercase ${r.color}`}>
                        {r.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-cream">{l.name}</td>
                    <td className="px-4 py-3">
                      <a href={l.contact.includes("@") ? `mailto:${l.contact}` : `tel:${l.contact}`} className="amber-link">
                        {l.contact}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-cream/70">{l.location || "—"}</td>
                    <td className="px-4 py-3 text-cream/70 max-w-xs truncate" title={l.message || ""}>{l.message || "—"}</td>
                    <td className="px-4 py-3 text-cream/40 text-xs">{l.source || "—"}</td>
                    <td className="px-4 py-3 text-xs" data-testid={`lead-attribution-${l.id}`}>
                      {l.utm_source || l.source_type ? (
                        <span className="text-cream/70">
                          {l.utm_source || SOURCE_TYPE_LABELS[l.source_type] || l.source_type}
                          {l.utm_campaign && <span className="text-cream/40"> · {l.utm_campaign}</span>}
                        </span>
                      ) : (
                        <span className="text-cream/30">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-cream/40 mt-6">
          Viser {leads.length} {leads.length === 1 ? "lead" : "leads"} · oppdatert {new Date().toLocaleTimeString("nb-NO")}
        </p>
      </div>
      )}
    </main>
  );
}
